import React, { useCallback, useEffect, useState } from 'react';
import CenterSpinner from '../../../../../../general/Loadings/CenterSpinner';
import NameAndDescription from '../connectedAccount/NameAndDescription';
import SampleData from '../connectedAccount/SampleData';
import Objects from '../connectedAccount/Objects';
import { toast } from "react-toastify";
import { callExternalDataApi } from '../../../../../../../apiCaller';
import { API_CUSTOM_EXTERNAL_DATA_CONNECTION, TYPE_SHOW_UNSAVE_CHANGE } from '../../../../../../../constants';
import { useDispatch, useSelector } from 'react-redux';
import { setRuleHaveEditting } from '../../../../../../../actions/common';

export const ConnectedAccountContext = React.createContext({});

const fetchConnectionDetail = async (id) => {
    try {
        const response = await callExternalDataApi(`${API_CUSTOM_EXTERNAL_DATA_CONNECTION}/${id}`, 'GET');

        const isSuccess = response?.data?.code === 200;
        const responseData = response?.data?.data; 

        if (isSuccess) {
            return responseData;
        } else {
            throw new Error(response?.data?.message || 'Failed to fetch connection detail.');
        }
    } catch (err) {
        throw err;
    }
};

const ConnectedAccount = ({ id, refetching, setRefetching }) => {
    const dispatch = useDispatch();
    const flexibleModal = useSelector((state) => state.theme.flexibleModal);
    const [isLoading, setIsLoading] = useState(false);
    const [activeStep, setActiveStep] = useState(1);
    const [stepsData, setStepsData] = useState({});
    const [initStepsData, setInitStepsData] = useState({});
    const [stepSubmitSnapshot, setStepSubmitSnapshot] = useState({});
    const [dirtySteps, setDirtySteps] = useState({});

    const uploadStatus = useSelector(
        state => state.subscriber.customExternalData?.pusher?.connection?.uploadStatusByConnectionId?.[id]
    );
    const canUpdate = uploadStatus?.status ? !['', 'done', 'error'].includes(uploadStatus?.status) : false;

    useEffect(() => {
        setActiveStep(1);
        setStepsData({});
        setInitStepsData({});
        setStepSubmitSnapshot({});
        setDirtySteps({});
    }, [flexibleModal]);

    useEffect(() => {
        setActiveStep(1);
        setStepsData({});
        setInitStepsData({});
        setIsLoading(true);

        if (!id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        fetchConnectionDetail(id)
            .then((data) => {
                setActiveStep(1);
                setStepsData(data);
                setInitStepsData(data);
            })
            .catch((err) => {
                toast.error(
                    err?.message ||
                    'An error occurred while fetching the connection detail!'
                );
            })
            .finally(() => setIsLoading(false));
    }, [id]);

    const setStepDirty = useCallback((step, dirty) => {
        setDirtySteps((prev) => {
            const next = { ...prev, [step]: dirty };
            const show = Object.values(next).some(Boolean);
            dispatch(
                setRuleHaveEditting({
                    show,
                    type: show ? TYPE_SHOW_UNSAVE_CHANGE.CREATE_SIMPLE : "",
                })
            );
            return next;
        });
    }, [dispatch]);

    return (
        <React.Suspense fallback={<CenterSpinner />}>
            <div className="rule-detail account-connected-detail">
                {isLoading ? (
                    <CenterSpinner />
                ) : (
                    <div className="cvr-create-new">
                        <h1>{id ? `Edit: External Data Set: ${stepsData.name || ""}` : `Create: External Data Set`}</h1>
                        <p>Manage the settings to receive a recurring data set into ListenLayer's platform. Each data set can accept one format of structured data that contains either event or object information. Multiple objects can be identified and extracted from each data set.</p>

                        <ConnectedAccountContext.Provider
                            value={{
                                id,
                                activeStep,
                                setActiveStep,
                                stepsData,
                                setStepsData,
                                initStepsData,
                                setInitStepsData,
                                refetching,
                                setRefetching,
                                stepSubmitSnapshot,
                                setStepSubmitSnapshot,
                                onFormDirty: setStepDirty,
                                dirtySteps,
                                setDirtySteps,
                                canUpdate,
                            }}
                        >
                            <NameAndDescription />
                            <SampleData />
                            <Objects />
                        </ConnectedAccountContext.Provider>
                    </div>
                )}
            </div>
        </React.Suspense>
    );
};

export default ConnectedAccount;
